/**
 * @link https://arashivision.feishu.cn/wiki/Fm4yw8r5kitAPqkAhescZ2vJnlf
 */

import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import cloneDeep from 'lodash/cloneDeep';
import { isEmpty } from '@/utils';
import { AS_COUNTRY_MAIN, TC_COUNTRY } from '@/utils/configs/public.config';
import CampaignBase from '@/components/activity/CampaignBase';

class GiftTShirt extends CampaignBase {
  constructor(props) {
    super(props);
    this.isProd = props.isProd;
    this.LANG = props.LANG;
    this.country = props.country;
    this.pageCommonConfig = props.pageCommonConfig;
    this.bindServiceDiscountMap = {};
    this.needTShirt = false;

    // 活动地区 - 亚太
    this.enabledArea = AS_COUNTRY_MAIN;

    /**
      北京时间：2025/3/20 16:00 - 4/1 16:00
      地区：TW HK MO
      语言：TC EN

      北京时间：2025/3/20 16:00 - 4/8 16:00
      地区：SG MY ID TH PH VN BN
      语言：EN
     */
    this.timeStampConfig = {
      'now': new Date().getTime(),
      'start': 0, // 开始时间
      'end': TC_COUNTRY.includes(props.country?.code) ? 1743494400000 : 1744099200000, // 结束时间
    };

    // 参与送赠的产品列表
    this.enableProducts = [19, 13, 24, 25];

    // 参与送增的T恤套餐列表
    this.sizeList = [
      {
        size: 'S',
        cid: 3486,
      },
      {
        size: 'M',
        cid: 3487,
      },
      {
        size: 'L',
        cid: 3488,
      },
    ];

    // T恤选择数据本地缓存
    this.cacheKey = '2025_cart_items_ts';
  }

  // 是否赠送T恤（产品页、购物车、结账页）
  enableGiftTShirt = ({ pid = '' }) => {
    return (
      this.enableProducts.includes(pid) &&
      this.enabledArea.includes(this.country?.code) &&
      this.timeStampConfig.now > this.timeStampConfig.start &&
      this.timeStampConfig.now < this.timeStampConfig.end
    );
  };

  isOutOfStock = (commodity) => {
    return commodity?.stock <= 0 || commodity?.sale_state < 0;
  };

  getSize = ({ size = '', cid }) => {
    if (size) {
      return this.sizeList.find((o) => o.size === size);
    }

    if (cid) {
      return this.sizeList.find((o) => o.cid === cid);
    }

    return this.sizeList[0];
  };

  // 获取T恤列表套餐信息
  getProductCommodities = ({ product }) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const sizeListCids = this.sizeList.map((o) => o.cid);
        // 获取T恤赠品列表
        const {
          data: { commodities = [] },
        } = await product.getCommodityPricesTemp(sizeListCids);
        this.sizeList.forEach((size) => (size.commodity = commodities.find((o) => o.id === size.cid)));

        // 按照配置定义的顺序来返回套餐
        const sizeCommodity = cloneDeep(this.sizeList)
          .filter((o) => !!o.commodity)
          .map((o) => o.commodity);
        resolve(sizeCommodity);
      } catch (e) {
        reject(e);
      }
    });
  };

  // 获取当前套餐匹配的有效赠品
  getCartMatchGifts = ({ cartItem, TShirtList }) => {
    const sizeCache = this.getProductSelectSizeCache();
    try {
      // 非送T恤产品
      const isEnabledProductGift = this.enableGiftTShirt({ cid: cartItem.commodityId, pid: cartItem.product });
      if (!isEnabledProductGift) return;

      // 用户选择不需要T恤
      const curSizeCid = sizeCache[cartItem.commodityId];
      if (curSizeCid === -1) return;

      // 首个非缺货的T恤
      const firstSalesTShirt = TShirtList.find((o) => !this.isOutOfStock(o));
      const curSize = TShirtList.find((o) => o.id === curSizeCid);
      const curSizeSales = !this.isOutOfStock(curSize);
      const curGifts = curSize && curSizeSales ? curSize : !isEmpty(firstSalesTShirt) ? firstSalesTShirt : null;
      return curGifts;
    } catch (e) {
      armsErrorSend('T恤-匹配有效赠品失败', {
        filename: 'TShirtGiftsConfig',
        data: { cartItem, sizeCache, msg: e.message },
      });
    }
  };

  // 购物车套餐匹配赠品
  handleCartItemGifts = ({ newCartItems, product }) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const sizeCache = this.getProductSelectSizeCache();
      try {
        // 非生效地区不匹配
        if (!this.enabledArea.includes(this.country?.code)) {
          resolve(newCartItems);
          return;
        }

        const TShirtList = await this.getProductCommodities({ product });

        // 判断是否在生效活动时间内且为支持赠品产品
        newCartItems.forEach((c) => {
          const curGifts = this.getCartMatchGifts({ cartItem: c, TShirtList });
          c.itemInteriors = [];
          if (curGifts) {
            c.itemInteriors.push(curGifts);
            this.setProductSelectSizeCache({
              curCartCommodity: c,
              sizeCid: curGifts?.id,
            });
          }
        });

        resolve(newCartItems);
      } catch (e) {
        armsErrorSend('T恤-购物车套餐匹配赠品', {
          filename: 'TShirtGiftsConfig',
          data: { newCartItems, msg: e.message, sizeCache },
        });
        resolve(newCartItems);
      }
    });
  };

  // 结账页套餐匹配赠品
  handleOrderItemGifts = ({ orderConfirmItems, product }) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const sizeCache = this.getProductSelectSizeCache();

      try {
        // 非生效地区不匹配
        if (!this.enabledArea.includes(this.country?.code)) {
          resolve([]);
          return;
        }

        const TShirtList = await this.getProductCommodities({ product });
        const giftTShirtItems = [];

        // 判断是否在生效活动时间内且为支持赠品产品
        orderConfirmItems.forEach((c) => {
          const curGifts = this.getCartMatchGifts({ cartItem: c, TShirtList });
          if (curGifts) {
            const hasGift = giftTShirtItems.find((o) => o?.commodity?.id === curGifts?.id);
            if (hasGift) {
              hasGift.number += c.count;
            } else {
              giftTShirtItems.push({
                commodity: curGifts,
                number: c.count,
                from: 'gift_t_shirt',
              });
            }
          }
        });

        resolve(giftTShirtItems);
      } catch (e) {
        armsErrorSend('T恤-结账页套餐匹配赠品', {
          filename: 'TShirtGiftsConfig',
          data: { orderConfirmItems, msg: e.message, sizeCache },
        });
        resolve([]);
      }
    });
  };

  // 结账前封装赠品 && 用户选择偏好记录
  handleOrderCreateGifts = ({ orderData, product }) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const sizeCache = this.getProductSelectSizeCache() ?? {};

      // 封装备注
      const handleRemark = ({ TShirtList = [] }) => {
        let remark = '';
        orderData.items.forEach((c) => {
          if (!this.enableGiftTShirt({ pid: c.pid, cid: c.commodityId })) return;
          const curGifts = this.getCartMatchGifts({
            cartItem: { product: c.pid, commodityId: c.commodityId },
            TShirtList,
          });
          const curSizeCid = sizeCache[c.id];
          const giftName = curSizeCid === -1 ? '不需要' : curGifts?.info?.name ?? `${curSizeCid}【缺货】`;
          remark += giftName ? `${c.id}-${giftName}；` : '';
          c.tCid = curGifts?.id;
          c.tPid = curGifts?.product;
        });

        if (remark) orderData.remark = remark;
      };

      try {
        // 中国大陆不送
        if (this.country?.code === 'CN') {
          resolve(orderData);
          return;
        }
        const TShirtList = await this.getProductCommodities({ product });
        if (!TShirtList?.length) {
          resolve(orderData);
          throw new Error('T恤套餐列表获取失败');
        }
        orderData.itemInteriors = [];

        // 处理备注
        handleRemark({ TShirtList });

        // 处理赠品
        orderData.items.forEach((c) => {
          const curGifts = this.getCartMatchGifts({
            cartItem: { product: c.pid, commodityId: c.commodityId },
            TShirtList,
          });
          if (curGifts) {
            const hasGift = orderData.itemInteriors.find((o) => o?.id === curGifts?.id);
            if (hasGift) {
              hasGift.number += c.number;
            } else {
              orderData.itemInteriors.push({
                pid: curGifts.product,
                id: curGifts.id,
                number: c.number,
              });
            }
          }
        });
        resolve(orderData);
      } catch (e) {
        armsErrorSend('T恤-结账封装赠品失败', {
          filename: 'TShirtGiftsConfig',
          data: { orderData, msg: e.message, sizeCache },
        });

        const TShirtList = await this.getProductCommodities({ product });
        // 处理备注
        handleRemark({ TShirtList });

        // 处理赠品
        orderData.itemInteriors = [];
        orderData.itemInteriorsRemark = 'e';
        orderData.items.forEach((c) => {
          if (!c?.tCid) return;
          const hasGift = orderData.itemInteriors.find((o) => o.id === c.tCid);
          if (hasGift) {
            hasGift.number += c.number;
          } else {
            orderData.itemInteriors.push({
              pid: c.tPid,
              id: c.tCid,
              number: c.number,
            });
          }
        });

        resolve(orderData);
      }
    });
  };

  // 记录当前产品页选中的尺寸
  setProductSelectSize = (selectedSize) => {
    this.sizeList.forEach((o) => {
      o.selected = o.cid === selectedSize.cid && !!selectedSize.need;
    });
    this.needTShirt = !!selectedSize.need;
  };

  getProductSelectSizeCache = () => {
    try {
      return JSON.parse(window.localStorage.getItem(this.cacheKey) ?? '{}');
    } catch (e) {
      window.localStorage.removeItem(this.cacheKey);
      armsErrorSend('T恤-获取赠品缓存失败', {
        filename: 'TShirtGiftsConfig',
        data: { e },
      });
      return {};
    }
  };

  // 记录当前选购的sku与T恤SKU的绑定关系
  setProductSelectSizeCache = ({ curSelectCommodity, curCartCommodity, sizeCid }) => {
    if (
      !this.enableGiftTShirt({
        pid: curSelectCommodity?.product ?? curCartCommodity?.product,
        cid: curCartCommodity?.commodityId,
      })
    )
      return;
    const selectCache = this.getProductSelectSizeCache();
    const selectSize = this.sizeList.find((o) => o.selected);
    if (curSelectCommodity) {
      if (this.needTShirt) {
        // 缓存不存在，可能是通过id方式/非产品页直接加入购物车的，则按顺序赠送当前生效的赠品
        if (!selectSize?.cid) {
          selectCache[curSelectCommodity.id] = this.sizeList[0].cid;
        } else {
          selectCache[curSelectCommodity.id] = selectSize?.cid ?? -1;
        }
      } else {
        selectCache[curSelectCommodity.id] = -1;
      }
    }
    if (curCartCommodity) {
      selectCache[curCartCommodity.commodityId] = sizeCid ?? -1;
    }
    window.localStorage.setItem(this.cacheKey, JSON.stringify(selectCache));
  };

  // 重新加购当前的SKU，重置删除或选择不要的标记
  restoreCurSkuSizeCache = ({ curSelectCommodity }) => {
    const selectCache = this.getProductSelectSizeCache();
    try {
      for (const selectCacheKey in selectCache) {
        if (selectCache[selectCacheKey] === -1 && curSelectCommodity === Number(selectCacheKey)) {
          delete selectCache[selectCacheKey];
        }
      }
      window.localStorage.setItem(this.cacheKey, JSON.stringify(selectCache));
    } catch (e) {
      window.localStorage.removeItem(this.cacheKey);
      armsErrorSend('T恤-restoreSizeCache', {
        filename: 'TShirtGiftsConfig',
        data: { e, selectCache },
      });
    }
  };
}

export default GiftTShirt;
